/**
 * BCP Form State Management Hook
 * Centralizes form data, validation, and field operations
 */

import { useState, useCallback, useMemo } from 'react';
import type { BCPFormData, BCPValidationError, BCPSection } from '../types';
import { validateBCPSections } from '../utils/bcp-transform';

export interface BCPFormStateOptions {
  initialData?: Partial<BCPFormData>;
  sections: BCPSection[];
}

export interface BCPFormState {
  // Form Data
  formData: BCPFormData;
  errors: BCPValidationError[];
  
  // Computed State
  isValid: boolean;
  hasChanges: boolean;
  
  // Actions
  updateField: (field: keyof BCPFormData, value: string) => void;
  validateField: (field: keyof BCPFormData) => boolean;
  validateAll: () => boolean;
  resetForm: (newData?: Partial<BCPFormData>) => void;
  clearErrors: () => void;
  
  // Field Helpers
  getFieldError: (field: keyof BCPFormData) => string | undefined;
  getFieldValue: (field: keyof BCPFormData) => string;
}

const createEmptyFormData = (): BCPFormData => ({
  companyInfo: '',
  continuityPolicy: '',
  riskAnalysis: '',
  criticalBusinesses: '',
  recoveryPlan: '',
  financialPlan: '',
  stockpilePlan: '',
  alternativeMeasures: '',
  activationCriteria: '',
  emergencyOrganization: '',
  rolesResponsibilities: '',
  communicationMethods: '',
  regularTraining: '',
  bcpReview: '',
  employeeEducation: ''
});

export const useBCPFormState = ({ 
  initialData = {}, 
  sections 
}: BCPFormStateOptions): BCPFormState => {
  const [formData, setFormData] = useState<BCPFormData>(() => ({
    ...createEmptyFormData(),
    ...initialData
  }));
  
  const [errors, setErrors] = useState<BCPValidationError[]>([]);
  const [originalData] = useState<BCPFormData>(() => ({
    ...createEmptyFormData(),
    ...initialData
  }));
  
  // Update field value
  const updateField = useCallback((field: keyof BCPFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user types
    setErrors(prev => prev.filter(error => error.field !== field));
  }, []);
  
  // Validate single field
  const validateField = useCallback((field: keyof BCPFormData): boolean => {
    const section = sections.find(s => s.id === field);
    if (!section) return true;
    
    const value = formData[field];
    const fieldErrors: BCPValidationError[] = [];
    
    // Required validation
    if (section.required && (!value || value.trim().length === 0)) {
      fieldErrors.push({
        field,
        message: `${section.title}は必須項目です`
      });
    }
    
    // Min length validation
    if (section.minLength && value && value.length < section.minLength) {
      fieldErrors.push({
        field,
        message: `${section.title}は${section.minLength}文字以上で入力してください`
      });
    }
    
    // Max length validation
    if (section.maxLength && value && value.length > section.maxLength) {
      fieldErrors.push({
        field,
        message: `${section.title}は${section.maxLength}文字以内で入力してください`
      });
    }
    
    // Update errors
    setErrors(prev => [
      ...prev.filter(error => error.field !== field),
      ...fieldErrors
    ]);
    
    return fieldErrors.length === 0;
  }, [formData, sections]);
  
  // Validate all fields
  const validateAll = useCallback((): boolean => {
    const allErrors: BCPValidationError[] = [];
    
    sections.forEach(section => {
      const value = formData[section.id];
      
      // Required validation
      if (section.required && (!value || value.trim().length === 0)) {
        allErrors.push({
          field: section.id,
          message: `${section.title}は必須項目です`
        });
      }
      
      // Min length validation
      if (section.minLength && value && value.length < section.minLength) {
        allErrors.push({
          field: section.id,
          message: `${section.title}は${section.minLength}文字以上で入力してください`
        });
      }
      
      // Max length validation
      if (section.maxLength && value && value.length > section.maxLength) {
        allErrors.push({
          field: section.id,
          message: `${section.title}は${section.maxLength}文字以内で入力してください`
        });
      }
    });
    
    // Add business logic validation
    const businessErrors = validateBCPSections(formData);
    allErrors.push(...businessErrors);
    
    setErrors(allErrors);
    return allErrors.length === 0;
  }, [formData, sections]);
  
  // Reset form
  const resetForm = useCallback((newData?: Partial<BCPFormData>) => {
    const resetData = {
      ...createEmptyFormData(),
      ...newData
    };
    setFormData(resetData);
    setErrors([]);
  }, []);
  
  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);
  
  // Get field error message
  const getFieldError = useCallback((field: keyof BCPFormData): string | undefined => {
    return errors.find(error => error.field === field)?.message;
  }, [errors]);
  
  // Get field value
  const getFieldValue = useCallback((field: keyof BCPFormData): string => {
    return formData[field] || '';
  }, [formData]);
  
  // Computed values
  const isValid = useMemo(() => errors.length === 0, [errors]);
  
  const hasChanges = useMemo(() => {
    return Object.keys(formData).some(key => {
      const field = key as keyof BCPFormData;
      return formData[field] !== originalData[field];
    });
  }, [formData, originalData]);
  
  return {
    // Form Data
    formData,
    errors,
    
    // Computed State
    isValid,
    hasChanges,
    
    // Actions
    updateField,
    validateField,
    validateAll,
    resetForm,
    clearErrors,
    
    // Field Helpers
    getFieldError,
    getFieldValue,
  };
};